import { useMemo, useState } from "react";
import { Calculator, TrendingDown } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { DESIRED_PACKAGES, PACKAGE_GROUPS } from "../constants/config";

const ADMIN_FEE = 7500;
const PPN = 0.11;
const fmt = (n) => `Rp ${Math.round(n).toLocaleString("id-ID")}`;

const PRICE_MAP = PACKAGE_GROUPS.flatMap((g) =>
  g.items.map((i) => ({ key: `${g.name} — ${i.speed}`, price: i.price }))
);

export const SavingsCalculator = () => {
  const [selected, setSelected] = useState("");
  const [currentBill, setCurrentBill] = useState("");

  const result = useMemo(() => {
    const pkg = PRICE_MAP.find((p) => p.key === selected);
    if (!pkg) return null;
    const subtotal = pkg.price + ADMIN_FEE;
    const tax = subtotal * PPN;
    const total = subtotal + tax;
    const current = parseInt(currentBill.replace(/\D/g, ""), 10);
    const savings = !isNaN(current) && current > total ? current - total : null;
    return { price: pkg.price, subtotal, tax, total, savings };
  }, [selected, currentBill]);

  return (
    <div
      data-testid="savings-calculator"
      className="mt-10 bg-white rounded-2xl border-2 border-[#E60000]/20 p-7 md:p-10"
    >
      <div className="flex items-center gap-3">
        <span className="w-12 h-12 rounded-xl bg-[#E60000] flex items-center justify-center shrink-0">
          <Calculator className="w-6 h-6 text-white" strokeWidth={2} />
        </span>
        <div>
          <h3 className="font-heading text-lg md:text-xl font-bold text-gray-900">
            Kalkulator Total Tagihan
          </h3>
          <p className="text-sm text-gray-500">
            Hitung total tagihan per bulan sudah termasuk biaya admin Rp 7.500
            dan PPN 11%.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mt-7">
        <div className="space-y-2">
          <Label>Paket Retensi yang Diinginkan</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="calc-package-select">
              <SelectValue placeholder="Pilih paket" />
            </SelectTrigger>
            <SelectContent>
              {DESIRED_PACKAGES.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="calc-current-bill">Tagihan Kamu Sekarang (opsional)</Label>
          <Input
            id="calc-current-bill"
            data-testid="calc-current-bill-input"
            placeholder="Contoh: 450000"
            inputMode="numeric"
            value={currentBill}
            onChange={(e) => setCurrentBill(e.target.value)}
          />
        </div>
      </div>

      {result && (
        <div data-testid="calc-result" className="mt-7 border-t border-dashed border-gray-200 pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            <div>
              <p className="text-xs text-gray-400 font-medium">Harga Paket</p>
              <p className="font-heading font-bold text-gray-900 mt-1" data-testid="calc-price">
                {fmt(result.price)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 font-medium">Biaya Admin</p>
              <p className="font-heading font-bold text-gray-900 mt-1" data-testid="calc-admin">
                {fmt(ADMIN_FEE)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 font-medium">PPN 11%</p>
              <p className="font-heading font-bold text-gray-900 mt-1" data-testid="calc-ppn">
                {fmt(result.tax)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400 font-medium">Total Tagihan/Bulan</p>
              <p className="font-heading font-black text-xl text-[#E60000] mt-0.5" data-testid="calc-total">
                {fmt(result.total)}
              </p>
            </div>
          </div>

          {result.savings !== null && (
            <div
              data-testid="calc-savings"
              className="mt-6 bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center gap-3"
            >
              <TrendingDown className="w-5 h-5 text-green-600 shrink-0" />
              <p className="text-sm text-green-800">
                Kamu hemat{" "}
                <span className="font-bold">{fmt(result.savings)}/bulan</span>{" "}
                atau sekitar{" "}
                <span className="font-bold">{fmt(result.savings * 12)}/tahun</span>{" "}
                dibanding tagihan sekarang.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
